import { Icons } from '@usepepper/sprout-kit-ui';
import { LayoutSize, useLayoutContext } from '@/contexts/layout-context';
import {
  MicroProduct,
  MicroProductsSlot,
  useMicroProductsLoader,
} from '@domain/microproducts/loaders/use-microproducts-loader';
import { NavItemIcon } from '@/domain/navigation/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { useEffect, useMemo } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSalsaManifest } from '@domain/microproducts/loaders/use-salsa-manifest';

export interface SalesHubSidebarNavItem<
  T extends keyof SalesPortalStackParams = keyof SalesPortalStackParams,
> {
  /** Icon component, resolved from the product's `displayIconName`. */
  icon?: NavItemIcon;
  /** Icon URL fallback when no named icon resolves. */
  iconUrl?: string;
  /** Product ID — the cross-app contract. */
  id: string;
  /** Whether this nav item is active for the current route. */
  isActive: boolean;
  /** Label for the nav item. */
  label: string;
  /** Optional params for the route navigation. */
  params?: SalesPortalStackParams[T];
  /** Native route. Absent for SPA microproducts (which navigate to Base). */
  route?: T;
}

/** Config cannot enable a product that this build has no phone layout for. */
type MobileSupport = 'unsupported' | 'always' | 'opt-in';

/**
 * A product whose screen is coded directly into the Restaurant App.
 */
interface NativeProduct {
  activeRouteNames: string[];
  mobileSupport: MobileSupport;
  route: keyof SalesPortalStackParams;
}

const NATIVE_PRODUCTS: Record<string, NativeProduct> = {
  accounts: {
    activeRouteNames: [
      SALES_PORTAL_ROUTES.AccountList,
      SALES_PORTAL_ROUTES.AccountDetail,
      SALES_PORTAL_ROUTES.EmployeeList,
    ],
    mobileSupport: 'always',
    route: SALES_PORTAL_ROUTES.AccountList,
  },
  copilot: {
    activeRouteNames: [SALES_PORTAL_ROUTES.Copilot],
    mobileSupport: 'unsupported',
    route: SALES_PORTAL_ROUTES.Copilot,
  },
  insights: {
    activeRouteNames: [SALES_PORTAL_ROUTES.Insights],
    mobileSupport: 'opt-in',
    route: SALES_PORTAL_ROUTES.Insights,
  },
  'order-entry': {
    activeRouteNames: [SALES_PORTAL_ROUTES.OrderEntry],
    mobileSupport: 'always',
    route: SALES_PORTAL_ROUTES.OrderEntry,
  },
  'order-guides': {
    activeRouteNames: [SALES_PORTAL_ROUTES.OrderGuides],
    mobileSupport: 'unsupported',
    route: SALES_PORTAL_ROUTES.OrderGuides,
  },
  'price-quotes': {
    activeRouteNames: [SALES_PORTAL_ROUTES.PriceQuotesBuilder],
    mobileSupport: 'unsupported',
    route: SALES_PORTAL_ROUTES.PriceQuotesBuilder,
  },
  prospects: {
    activeRouteNames: [SALES_PORTAL_ROUTES.ProspectFinder],
    mobileSupport: 'unsupported',
    route: SALES_PORTAL_ROUTES.ProspectFinder,
  },
  'prospects-v2': {
    activeRouteNames: [
      SALES_PORTAL_ROUTES.Prospects,
      SALES_PORTAL_ROUTES.ProspectDetail,
      SALES_PORTAL_ROUTES.ProspectEmployeeList,
    ],
    mobileSupport: 'unsupported',
    route: SALES_PORTAL_ROUTES.Prospects,
  },
  superintelligence: {
    activeRouteNames: [SALES_PORTAL_ROUTES.Superintelligence],
    mobileSupport: 'opt-in',
    route: SALES_PORTAL_ROUTES.Superintelligence,
  },
};

/**
 * A business organization opts a product into phones with the `mobile_enabled` client prop on its config.
 */
const isMobileEnabled = (product: MicroProduct): boolean =>
  product.props.mobile_enabled === true;

interface UseSalesHubSidebarNavItemsLoaderOptions {
  onComplete?: (navItems: SalesHubSidebarNavItem[]) => void;
}

interface UseSalesHubSidebarNavItemsLoaderResult {
  error: Error | null;
  loading: boolean;
  navItems: SalesHubSidebarNavItem[];
}

/**
 * Resolves a product's icon, preferring a named Sprout Kit icon and falling
 * back to its icon URL.
 *
 * @param product - The product to resolve an icon for.
 * @returns An `icon` component, or an `iconUrl` when no named icon resolves.
 */
const resolveIcon = (
  product: MicroProduct,
): { icon?: NavItemIcon; iconUrl?: string } => {
  if (product.displayIconName) {
    const icons = Icons as Record<string, NavItemIcon | undefined>;
    const icon = icons[product.displayIconName];
    if (icon) {
      return { icon };
    }
  }
  return { iconUrl: product.displayIconUrl ?? undefined };
};

/**
 * Maps a product to a sidebar nav item: a native route when its id is in
 * NATIVE_PRODUCTS, otherwise a Salsa SPA routed through Base.
 *
 * @param product - The product to map.
 * @param currentRouteName - The active route name, used to compute `isActive`.
 * @param currentProductId - The active Base-route `productId`, for SPA active state.
 * @returns The sidebar nav item.
 */
const toNavItem = (
  product: MicroProduct,
  currentRouteName: string,
  currentProductId: string | undefined,
): SalesHubSidebarNavItem => {
  const native = NATIVE_PRODUCTS[product.id];
  const base = {
    ...resolveIcon(product),
    id: product.id,
    label: product.displayName,
  };

  // Native products built into Serrano
  if (native) {
    return {
      ...base,
      isActive: native.activeRouteNames.includes(currentRouteName),
      route: native.route,
    };
  }

  // Salsa Microproducts
  return {
    ...base,
    isActive:
      currentRouteName === SALES_PORTAL_ROUTES.Base &&
      currentProductId === product.id,
  };
};

/**
 * Loads the Sales Hub sidebar using the microproducts config.
 *
 * @returns The resolved sidebar nav items plus loading/error state.
 */
export const useSalesHubSidebarNavItemsLoader = ({
  onComplete,
}: UseSalesHubSidebarNavItemsLoaderOptions = {}): UseSalesHubSidebarNavItemsLoaderResult => {
  const { layoutSize } = useLayoutContext();
  const isSmallLayout = layoutSize === LayoutSize.SMALL;
  const route = useRoute();

  const {
    slots,
    loading: microProductsLoading,
    error: microProductsError,
  } = useMicroProductsLoader();
  const {
    availableProducts,
    loaded: manifestLoaded,
    loading: manifestLoading,
  } = useSalsaManifest();

  const navItems = useMemo<SalesHubSidebarNavItem[]>(() => {
    const sidebarSlot = slots.find(
      (s) => s.slot === MicroProductsSlot.SALES_HUB_SIDEBAR,
    );
    const products = sidebarSlot?.products ?? [];

    const currentRouteName = route.name;
    const currentProductId = (
      route.params as { productId?: string } | undefined
    )?.productId;

    return products
      .filter((product) => {
        const native = NATIVE_PRODUCTS[product.id];
        if (!native) {
          if (isSmallLayout && !isMobileEnabled(product)) {
            return false;
          }
          // Salsa also needs the manifest to confirm a deployed bundle
          return manifestLoaded && availableProducts.has(product.id);
        }

        if (!isSmallLayout) {
          return true;
        }
        if (native.mobileSupport === 'unsupported') {
          return false;
        }
        return native.mobileSupport === 'always' || isMobileEnabled(product);
      })
      .map((product) => toNavItem(product, currentRouteName, currentProductId));
  }, [
    slots,
    availableProducts,
    manifestLoaded,
    isSmallLayout,
    route.name,
    route.params,
  ]);

  const loading =
    microProductsLoading || manifestLoading || microProductsError !== null;

  // Call the onComplete callback once both the
  // useMicroProductsLoader and useSalsaManifest have finished.
  useEffect(() => {
    if (loading) {
      return;
    }
    onComplete?.(navItems);
  }, [loading, navItems, onComplete]);

  return {
    error: microProductsError ?? null,
    loading,
    navItems,
  };
};
